import React, { useState, useEffect, useMemo } from 'react';
import { Trophy, Plus, Eye, EyeOff, Calendar, TrendingUp, BarChart3 } from 'lucide-react';
import { loadUserInfo, loadBets, addBet, updateBet } from '../services/firebase';
import { sendBetToDiscord, sendBetResultToDiscord } from '../services/discord';
import { sports } from '../constants/data';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import Stats from './Stats';
import BetInput from './BetInput';
import BetCard from './BetCard';
import Reports from './Reports';

const BettingTracker = ({ user }) => {
  const [bets, setBets] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [activeTab, setActiveTab] = useState('my-bets');
  const [selectedSport, setSelectedSport] = useState('all');
  const [showPrivate, setShowPrivate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const [copiedBetData, setCopiedBetData] = useState(null);

  // Load user data and bets on mount
  useEffect(() => {
    if (user) {
      const fetchData = async () => {
        try {
          console.log('Loading data for user:', user);
          setLoading(true);
          
          // Load user info first
          const userData = await loadUserInfo(user.uid);
          console.log('User data loaded:', userData);
          setUserInfo(userData);
          
          // Load bets with user info
          const loadedBets = await loadBets(user, userData);
          console.log('Bets loaded:', loadedBets);
          setBets(loadedBets);
          
        } catch (error) {
          console.error('Error loading data:', error);
          toast.error('Failed to load data. Please try again.');
        } finally {
          setLoading(false);
        }
      };
      
      fetchData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const handleAddBet = async (betData) => {
    console.log('Adding bet:', betData);
    
    try {
      setLoading(true);
      
      // Add bet to Firebase
      const betId = await addBet(betData, user, userInfo);
      console.log('Bet added with ID:', betId);
      
      // Reload bets to get the latest data
      const updatedBets = await loadBets(user, userInfo);
      setBets(updatedBets);
      
      toast.success(`${betData.isTipsterBet ? 'Tip' : 'Bet'} added successfully!`);
      
      // Send to Discord if it's a public tipster bet
      if (betData.isPublic && betData.isTipsterBet) {
        try {
          await sendBetToDiscord(betData, user, userInfo);
          console.log('Bet posted to Discord');
        } catch (discordError) {
          console.error('Discord posting failed:', discordError);
          // Don't show error to user since bet was saved successfully
        }
      }
      
    } catch (error) {
      console.error('Error adding bet:', error);
      toast.error('Failed to add bet. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBet = async (id, updates) => {
    console.log('Updating bet:', id, updates);
    
    try {
      // Find the bet being updated for Discord notification
      const betToUpdate = bets.find(bet => bet.id === id);
      
      await updateBet(id, updates);
      
      // Reload bets to get updated data
      const updatedBets = await loadBets(user, userInfo);
      setBets(updatedBets);
      
      toast.success('Bet updated successfully!');
      
      // Send result to Discord if status changed
      if (updates.status && betToUpdate && ['won', 'lost', 'push'].includes(updates.status)) {
        try {
          await sendBetResultToDiscord(betToUpdate, updates.status);
          console.log('Bet result posted to Discord');
        } catch (discordError) {
          console.error('Discord result posting failed:', discordError);
          // Don't show error to user since bet was updated successfully
        }
      }
      
    } catch (error) {
      console.error('Error updating bet:', error);
      toast.error('Failed to update bet. Please try again.');
    }
  };

  const handleCopyBet = (bet) => {
    console.log('Copying bet:', bet);
    
    const copiedBet = {
      sport: bet.sport,
      league: bet.league,
      homeTeam: bet.homeTeam,
      awayTeam: bet.awayTeam,
      betType: bet.betType,
      selection: bet.selection,
      odds: bet.odds,
      units: bet.units,
      confidence: bet.confidence,
      tipster: bet.tipster,
      originalTipsterUid: bet.tipsterUid,
      isPublic: false, // Copied bets are private by default
      isTipsterBet: userInfo?.isTipster || false,
      placedOnTime: true,
      eventDate: bet.eventDateTime ? bet.eventDateTime.split('T')[0] : '',
      eventTime: bet.eventDateTime ? bet.eventDateTime.split('T')[1] : '',
      isMultiLeg: bet.isMultiLeg || false,
      legs: bet.legs || [],
      analysis: bet.analysis || ''
    };
    
    setCopiedBetData(copiedBet);
    setShowForm(true);
  };

  const filteredBets = useMemo(() => {
    console.log('Filtering bets:', { bets: bets.length, selectedSport, activeTab });
    
    return bets.filter(bet => {
      // Sport filter
      if (selectedSport !== 'all' && bet.sport !== selectedSport) {
        return false;
      }
      
      // Tab filter
      if (activeTab === 'public' && !bet.isPublic) {
        return false;
      }
      if (activeTab === 'private' && bet.isPublic) {
        return false;
      }
      if (activeTab === 'my-bets') {
        // Show only user's own bets
        return bet.tipsterUid === user?.uid || bet.userEmail === user?.email;
      }
      
      return true;
    });
  }, [bets, selectedSport, activeTab, user]);

  const todaysBets = useMemo(() => {
    const today = new Date().toLocaleDateString();
    return filteredBets.filter(bet => {
      if (bet.date === today) return true;
      if (bet.datePlaced) {
        const betDate = new Date(bet.datePlaced).toLocaleDateString();
        return betDate === today;
      }
      return false;
    });
  }, [filteredBets]);

  const myBets = useMemo(() => {
    return bets.filter(bet => 
      bet.tipsterUid === user?.uid || bet.userEmail === user?.email
    );
  }, [bets, user]);

  if (!user) {
    return (
      <div className="min-h-screen py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Trophy size={64} className="mx-auto text-gray-400 mb-6" />
          <h2 className="text-3xl font-bold mb-4">Login to Track Your Bets</h2>
          <p className="text-gray-600 mb-8">
            Sign in to start tracking your betting journey and view performance analytics.
          </p>
          <button
            onClick={() => window.location.href = '#login'}
            className="bg-gradient-to-r from-green-400 to-blue-400 text-white px-8 py-3 rounded-full font-semibold hover:opacity-90 transition"
          >
            Login Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-20">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white shadow-sm rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                Bet Tracker
              </h1>
              <p className="text-sm text-gray-600">
                {userInfo?.isTipster ? 'Track your tips and performance' : 'Monitor your betting performance'}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowReports(true)}
                className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition"
              >
                <BarChart3 size={20} />
                <span className="hidden sm:inline">Reports</span>
              </button>
              <button
                onClick={() => setShowPrivate(!showPrivate)}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition"
              >
                {showPrivate ? <Eye size={20} /> : <EyeOff size={20} />}
                <span className="text-sm hidden sm:inline">{showPrivate ? 'Hide' : 'Show'} Private</span>
              </button>
              <button
                onClick={() => {
                  setCopiedBetData(null);
                  setShowForm(true);
                }}
                className="bg-gradient-to-r from-green-400 to-blue-400 text-white px-4 py-2 rounded-lg hover:opacity-90 transition flex items-center gap-2"
              >
                <Plus size={20} />
                <span className="hidden sm:inline">Add {userInfo?.isTipster ? 'Tip' : 'Bet'}</span>
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <ClipLoader color="#10B981" size={50} />
            <p className="text-gray-600 mt-4">Loading your bets...</p>
          </div>
        ) : (
          <>
            {/* Stats Component - pass user's own bets for accurate stats */}
            <Stats bets={myBets} />

            {/* Navigation Tabs */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
              <div className="flex flex-col sm:flex-row gap-4 justify-between">
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => setActiveTab('my-bets')}
                    className={`px-4 py-2 rounded-lg transition ${
                      activeTab === 'my-bets'
                        ? 'bg-gradient-to-r from-green-400 to-blue-400 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    My {userInfo?.isTipster ? 'Tips' : 'Bets'} ({myBets.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('public')}
                    className={`px-4 py-2 rounded-lg transition ${
                      activeTab === 'public'
                        ? 'bg-gradient-to-r from-green-400 to-blue-400 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Public Tips
                  </button>
                  {showPrivate && (
                    <button
                      onClick={() => setActiveTab('private')}
                      className={`px-4 py-2 rounded-lg transition ${
                        activeTab === 'private'
                          ? 'bg-gradient-to-r from-green-400 to-blue-400 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Private
                    </button>
                  )}
                </div>
                <select
                  className="px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-300"
                  value={selectedSport}
                  onChange={(e) => setSelectedSport(e.target.value)}
                >
                  <option value="all">All Sports</option>
                  {sports.map(sport => (
                    <option key={sport} value={sport}>{sport}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Today's Bets Section */}
            {todaysBets.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Calendar size={20} />
                  Today's {activeTab === 'my-bets' ? (userInfo?.isTipster ? 'Tips' : 'Bets') : 'Tips'} ({todaysBets.length})
                </h2>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {todaysBets.map(bet => (
                    <BetCard
                      key={bet.id}
                      bet={bet}
                      onUpdate={handleUpdateBet}
                      showPrivate={showPrivate}
                      user={user}
                      onCopyBet={handleCopyBet}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* All Bets Section */}
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <TrendingUp size={20} />
                {activeTab === 'my-bets' 
                  ? (userInfo?.isTipster ? 'Your Tipping History' : 'Your Betting History')
                  : activeTab === 'public' 
                    ? 'Public Tips' 
                    : 'Private Tips'
                } ({filteredBets.length})
              </h2>
              
              {filteredBets.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg">
                  <Trophy size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">
                    {activeTab === 'my-bets' 
                      ? `No ${userInfo?.isTipster ? 'tips' : 'bets'} found. Add your first one to get started!`
                      : `No ${activeTab} tips found.`
                    }
                  </p>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {filteredBets.map(bet => (
                    <BetCard
                      key={bet.id}
                      bet={bet}
                      onUpdate={handleUpdateBet}
                      showPrivate={showPrivate}
                      user={user}
                      onCopyBet={handleCopyBet}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Bet Input Modal */}
      {showForm && (
        <BetInput
          onSubmit={handleAddBet}
          onClose={() => {
            setShowForm(false);
            setCopiedBetData(null);
          }}
          user={{ 
            ...user, 
            userType: userInfo?.isTipster ? 'tipster' : 'punter',
            isTipster: userInfo?.isTipster 
          }}
          copiedBet={copiedBetData}
        />
      )}

      {/* Reports Modal */}
      {showReports && (
        <Reports
          bets={myBets} // Pass user's own bets for reports
          onClose={() => setShowReports(false)}
        />
      )}
    </div>
  );
};

export default BettingTracker;